import {ChangeDetectionStrategy, Component, inject, ViewChild} from '@angular/core';
import {MatFabButton} from '@angular/material/button';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {NamedRouterLinkDirective} from '@lib/directives/named-router-link/named-router-link.directive';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {BranchSelectors} from '@store/branches/branch.selectors';
import {createSelector, Store} from '@ngrx/store';
import {PatronActions} from '@store/patron/patron.actions';
import {RegistrationActions} from '@store/registration/registration.actions';
import {TruncateDirective} from '@aviary/directives/truncate.directive';
import {OrganizationSelectors} from '@store/organization/organization.selectors';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';
import {CdkConnectedOverlay, CdkOverlayOrigin} from '@angular/cdk/overlay';
import {MatCard, MatCardContent} from '@angular/material/card';
import {NotificationListComponent} from '@lib/components/notifications/notification-list.component';
import {NgTemplateOutlet} from '@angular/common';
import {NotificationsSelectors} from '@store/notifications/notifications.selectors';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';
import {filter} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MxSelector} from '@aviary/store/store.types';
import {LinkButtonComponent} from '@aviary/buttons/link-button/link-button.component';

@Component({
  selector: 'px-header-top-bar',
  host: {
    class: 'flex flex-row justify-between items-center h-[3.75rem] w-full ps-2 pe-3 lg:px-10 bg-surface3 border-b border-outline-variant'
  },
  templateUrl: './header-top-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatMenu, MatMenuItem, MatMenuTrigger, NamedRouterLinkDirective, SelectPipe, MatFabButton, TruncateDirective, CdkOverlayOrigin, CdkConnectedOverlay, MatCard,
    MatCardContent, NotificationListComponent, NgTemplateOutlet, TextButtonComponent, IconButtonComponent, FilledButtonComponent, LinkButtonComponent
  ]
})
export class HeaderTopBarComponent {
  @ViewChild('hamburgerButton') public hamburgerButton?: MatMenuTrigger;

  public readonly branchSelectors = BranchSelectors.Auth;
  public readonly patronSelectors = PatronSelectors.Auth;
  public readonly organizationSelectors = OrganizationSelectors.Auth;
  public readonly viewportSelectors = ViewportSelectors;
  public readonly notificationsSelectors = NotificationsSelectors.Notifications;
  public notificationPanelOpened: boolean = false;

  // Single source of knowledge about when the hamburger menu should be included in the UI.
  public readonly includeHamburgerMenu: MxSelector<boolean> = createSelector(
    this.viewportSelectors.isLgAndUp,
    isLgOrAbove => !isLgOrAbove
  );

  private readonly store: Store = inject(Store);

  constructor() {
    /*
      The template controls whether the hamburger button is rendered, but we still need to ensure the menu is removed along with the button if it was open.
    */
    this.store.select(this.includeHamburgerMenu)
      .pipe(
        filter(include => !include),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.hamburgerButton?.closeMenu();
      });
  }

  public onClickLogin(): void {
    this.store.dispatch(PatronActions.showLoginDialog());
  }

  public onClickCreateAccount(): void {
    this.store.dispatch(RegistrationActions.showCreateAccount());
  }

  public onClickSwitchProfile(): void {
    // open dependents menu dialog
  }

  public toggleNotificationPanel(): void {
    this.notificationPanelOpened = !this.notificationPanelOpened;
  }

  public closeNotificationPanel(): void {
    this.notificationPanelOpened = false;
  }
}
